import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import analytics from 'src/lib/player/analytics';
import { type SponsorLogo, useSponsorModule } from 'src/lib/player/store';
import { arrangeMiddleOut } from 'src/lib/player/utils/arrangeMiddleOut';
import { shuffleArray } from 'src/lib/player/utils/shuffleArray';
import { useElementDimensions } from 'src/lib/player/utils/useElementDimensions';
import { useImageWidths } from 'src/lib/player/utils/useImageWidths';

const ROTATION_INTERVAL_MS = 60_000; // 1 minute
const FADE_DURATION_MS = 300;
const LOGO_GAP_PX = 10; // Gap between logos (must match CSS)
const MAX_LOGO_WIDTH_PX = 300; // Max logo width (must match CSS)
const MAX_LOGO_WIDTH_MOBILE_PX = 192; // Max logo width on mobile (must match CSS)
const MOBILE_BREAKPOINT_PX = 750;

export const SponsorLogos: FC = () => {
  const sponsorModule = useSponsorModule();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementDimensions(containerRef);
  const [isVisible, setIsVisible] = useState(true);
  const [displayedLogos, setDisplayedLogos] = useState<Array<SponsorLogo>>([]);

  const logos = sponsorModule?.enabled ? sponsorModule.logos : null;
  const imageWidths = useImageWidths(logos);
  const isMobile = useIsMobile();
  const allWidthsLoaded = useAllWidthsLoaded(logos, imageWidths);

  const { selectedLogos } = useLogoSelection(logos, imageWidths, containerWidth, isMobile);

  // Arrange logos for display (widest in the middle)
  const arrangedLogos = useMemo(() => {
    const logosWithImages = selectedLogos.filter((logo) => logo.imageUrls?.logo != null);
    return arrangeMiddleOut(logosWithImages, (logo) => imageWidths[logo.id] ?? 0);
  }, [selectedLogos, imageWidths]);

  // Handle logo changes with fade transition
  useLogoTransition({
    arrangedLogos,
    displayedLogos,
    allWidthsLoaded,
    containerWidth,
    setDisplayedLogos,
    setIsVisible,
  });

  const handleSponsorLogoClick = useCallback(() => {
    analytics.send('sponsor-logo-click');
  }, []);

  if (sponsorModule == null || !sponsorModule.enabled) return null;

  return (
    <div
      ref={containerRef}
      className="sponsor-logos-container"
      style={{
        justifyContent: sponsorModule.alignment || 'left',
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
      }}
    >
      {displayedLogos.map((logo) => (
        <a
          key={logo.id}
          href={logo.url}
          title={logo.title}
          className="sponsor-logo-button"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleSponsorLogoClick}
        >
          <img src={logo.imageUrls?.logo} alt={logo.altText || logo.title || 'Sponsor logo'} />
        </a>
      ))}
    </div>
  );
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Track mobile breakpoint changes
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT_PX);

  useEffect(() => {
    const updateMobile = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT_PX);
    window.addEventListener('resize', updateMobile);
    return () => window.removeEventListener('resize', updateMobile);
  }, []);

  return isMobile;
}

/**
 * Check if all image widths are loaded for the given logos
 */
function useAllWidthsLoaded(
  logos: Array<SponsorLogo> | null,
  imageWidths: Record<string, number>,
): boolean {
  return useMemo(() => {
    if (!logos || logos.length === 0) return false;
    const logosWithImages = logos.filter((logo) => logo.imageUrls?.logo != null);
    if (logosWithImages.length === 0) return false;
    return logosWithImages.every((logo) => imageWidths[logo.id] > 0);
  }, [logos, imageWidths]);
}

/**
 * Handle logo selection with rotation support.
 * Shuffles logos only when rotation triggers or logos change.
 */
function useLogoSelection(
  logos: Array<SponsorLogo> | null,
  imageWidths: Record<string, number>,
  containerWidth: number,
  isMobile: boolean,
): { selectedLogos: Array<SponsorLogo>; needsRotation: boolean } {
  const [rotationKey, setRotationKey] = useState(0);
  const lastShuffledLogosRef = useRef<Array<SponsorLogo> | null>(null);
  const lastRotationKeyRef = useRef<number>(-1);
  const lastLogosRef = useRef<Array<SponsorLogo> | null>(null);

  // Calculate how many logos can fit
  const maxFittingCount = useMemo(() => {
    if (!logos || logos.length === 0 || containerWidth <= 0) return 0;
    return calculateFittingLogos(logos, imageWidths, containerWidth, isMobile).length;
  }, [logos, imageWidths, containerWidth, isMobile]);

  const needsRotation = logos != null && logos.length > maxFittingCount && maxFittingCount > 0;

  // Start rotation interval if needed
  useEffect(() => {
    if (!needsRotation) return;
    const interval = setInterval(() => setRotationKey((prev) => prev + 1), ROTATION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [needsRotation]);

  const selectedLogos = useMemo(() => {
    if (!logos || logos.length === 0 || maxFittingCount === 0) return [];
    if (!needsRotation) return logos;

    // Only re-shuffle if rotationKey or logos changed
    const shouldReshuffle =
      lastLogosRef.current !== logos ||
      lastRotationKeyRef.current !== rotationKey ||
      lastShuffledLogosRef.current == null;

    if (shouldReshuffle) {
      lastShuffledLogosRef.current = shuffleArray(logos);
      lastRotationKeyRef.current = rotationKey;
      lastLogosRef.current = logos;
    }

    return calculateFittingLogos(
      lastShuffledLogosRef.current || [],
      imageWidths,
      containerWidth,
      isMobile,
    );
  }, [logos, needsRotation, maxFittingCount, rotationKey, imageWidths, containerWidth, isMobile]);

  return { selectedLogos, needsRotation };
}

/**
 * Handle fade transitions when logos change
 */
function useLogoTransition({
  arrangedLogos,
  displayedLogos,
  allWidthsLoaded,
  containerWidth,
  setDisplayedLogos,
  setIsVisible,
}: {
  arrangedLogos: Array<SponsorLogo>;
  displayedLogos: Array<SponsorLogo>;
  allWidthsLoaded: boolean;
  containerWidth: number;
  setDisplayedLogos: (logos: Array<SponsorLogo>) => void;
  setIsVisible: (visible: boolean) => void;
}) {
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Wait for container width and all image widths before showing
    if (!allWidthsLoaded || containerWidth <= 0) return;

    if (arrangedLogos.length === 0) {
      setDisplayedLogos([]);
      hasInitializedRef.current = false;
      return;
    }

    // First time showing logos - show immediately without fade
    if (!hasInitializedRef.current) {
      setDisplayedLogos(arrangedLogos);
      setIsVisible(true);
      hasInitializedRef.current = true;
      return;
    }

    // Check if the set of logos changed (ignore reordering)
    const sameLogos = areSameLogoSets(displayedLogos, arrangedLogos);

    if (sameLogos) {
      // Same logos, just reordered - update without fade
      setDisplayedLogos(arrangedLogos);
      return;
    }

    // Different logos - fade out, then show new ones
    setIsVisible(false);
    const timeoutId = setTimeout(() => {
      setDisplayedLogos(arrangedLogos);
      setIsVisible(true);
    }, FADE_DURATION_MS);

    return () => clearTimeout(timeoutId);
  }, [
    arrangedLogos,
    displayedLogos,
    allWidthsLoaded,
    containerWidth,
    setDisplayedLogos,
    setIsVisible,
  ]);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if two logo arrays contain the same logos (ignoring order)
 */
function areSameLogoSets(logosA: Array<SponsorLogo>, logosB: Array<SponsorLogo>): boolean {
  if (logosA.length !== logosB.length) return false;
  const idsA = new Set(logosA.map((l) => l.id));
  return logosB.every((logo) => idsA.has(logo.id));
}

/**
 * Calculate the display width of a logo, capped by max-width CSS constraint
 */
function getDisplayWidth(naturalWidth: number, isMobile: boolean): number {
  const maxWidth = isMobile ? MAX_LOGO_WIDTH_MOBILE_PX : MAX_LOGO_WIDTH_PX;
  return Math.min(naturalWidth, maxWidth);
}

/**
 * Calculate which logos can fit in the container width
 */
function calculateFittingLogos(
  logos: Array<SponsorLogo>,
  imageWidths: Record<string, number>,
  containerWidth: number,
  isMobile: boolean,
): Array<SponsorLogo> {
  if (containerWidth <= 0 || logos.length === 0) return [];

  const result: Array<SponsorLogo> = [];
  let usedWidth = 0;

  for (const logo of logos) {
    const naturalWidth = imageWidths[logo.id] ?? 0;
    const displayWidth = getDisplayWidth(naturalWidth, isMobile);
    const widthNeeded = result.length > 0 ? displayWidth + LOGO_GAP_PX : displayWidth;

    if (usedWidth + widthNeeded <= containerWidth) {
      result.push(logo);
      usedWidth += widthNeeded;
    } else {
      break;
    }
  }

  return result;
}
